/**
 * Phase — Google Maps primitives (Map wrapper + Marker + InfoWindow).
 *
 * These wrap the raw google.maps API so business components stay
 * decoupled from the SDK. Add Roads/Weather extension points here
 * later — for now Maps JS only.
 */
import React, { useEffect, useRef, useState } from "react";
import { loadMaps } from "./loader";

/**
 * <GoogleMap markers=[{id,lat,lng,label?,info?}] fit={auto|controlled} center? zoom? fitNonce? focusMarkerId?>
 *
 * fit:
 *   - "auto" (default) — refit to bounds on every markers change.
 *     Suitable for previews (Dashboard mini-map) where viewport
 *     ownership is not user-driven.
 *   - "controlled" — never refit automatically on markers change.
 *     The caller triggers a refit by incrementing `fitNonce`. Suitable
 *     for the Fleet Map workspace where the user's pan/zoom must be
 *     preserved across telemetry refreshes.
 *
 * focusMarkerId (optional): id of a marker to open its InfoWindow +
 *   pan-to-marker. Bumping this prop drives external row-click focus.
 *
 * Empty state rendered by the CALLER (rows.length===0 branch).
 * `onError` fires with "maps_key_unavailable" | "maps_sdk_load_failed"
 * so surfaces can render their empty state and stop spinning.
 * `onMarkerClick(id)` fires when the user clicks any marker so the
 * caller can sync an external asset list.
 * `onUserInteract()` fires the first time the user drags/zooms the
 * map so the caller can flip to viewport-preservation mode without
 * needing internal Google Maps event refs.
 */
export function GoogleMap({
  markers = [],
  center,
  zoom = 6,
  fit = "auto",
  fitNonce,
  focusMarkerId,
  onError,
  onMarkerClick,
  onUserInteract,
  containerClassName = "",
  containerStyle,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerObjs = useRef([]);
  const infoWindow = useRef(null);
  const hasFitOnceRef = useRef(false);
  const [errored, setErrored] = useState(null);
  // Flips true once `new maps.Map(...)` has been constructed so the
  // marker-drawing effect below can re-run. Refs don't trigger effects
  // on their own, so without this the initial mount would race the
  // async SDK load and no markers would be drawn.
  const [mapReady, setMapReady] = useState(false);

  // Initialize map once when the SDK is ready.
  useEffect(() => {
    let cancelled = false;
    loadMaps()
      .then((maps) => {
        if (cancelled || !containerRef.current) return;
        const first = markers[0];
        const initCenter = center || (first ? { lat: first.lat, lng: first.lng } : { lat: 39.5, lng: -98.35 });
        mapRef.current = new maps.Map(containerRef.current, {
          center: initCenter,
          zoom,
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: "cooperative",
          clickableIcons: false,
        });
        infoWindow.current = new maps.InfoWindow();
        // Fleet Map UX Hardening — first user pan/zoom flips the caller
        // into viewport-preservation mode. Google fires dragstart on
        // drag; we also latch zoom_changed the first time it happens
        // after the initial fit. Both are one-shot listeners.
        const notify = () => onUserInteract?.();
        mapRef.current.addListener("dragstart", notify);
        // Guard zoom_changed with a "has been non-null initial zoom"
        // check so the fit-bounds initial zoom doesn't misfire.
        let initialZoomAt = mapRef.current.getZoom();
        mapRef.current.addListener("zoom_changed", () => {
          if (mapRef.current.getZoom() !== initialZoomAt) {
            initialZoomAt = mapRef.current.getZoom();
            notify();
          }
        });
        setMapReady(true);
      })
      .catch((e) => {
        if (cancelled) return;
        setErrored(e?.message || "maps_load_failed");
        onError?.(e?.message || "maps_load_failed");
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // (Re)draw markers when data changes. Fit behavior depends on `fit`:
  //   - "auto": refit on every data change (preview surfaces)
  //   - "controlled": only fit on first non-empty data OR when
  //     `fitNonce` changes (workspace surfaces that preserve viewport)
  useEffect(() => {
    if (!mapReady || !mapRef.current || !window.google?.maps) return;
    const maps = window.google.maps;
    markerObjs.current.forEach((m) => m.setMap(null));
    markerObjs.current = [];
    if (markers.length === 0) return;

    const bounds = new maps.LatLngBounds();
    markers.forEach((m) => {
      const marker = new maps.Marker({
        position: { lat: m.lat, lng: m.lng },
        map: mapRef.current,
        title: m.label || undefined,
      });
      marker.__aa_id = m.id;
      // InfoWindow AND external row-sync fire on the same click. Both
      // are safe when caller supplies neither.
      marker.addListener("click", () => {
        if (m.info) {
          infoWindow.current.setContent(m.info);
          infoWindow.current.open({ anchor: marker, map: mapRef.current });
        }
        onMarkerClick?.(m.id);
      });
      markerObjs.current.push(marker);
      bounds.extend({ lat: m.lat, lng: m.lng });
    });

    const isControlled = fit === "controlled";
    const isFirstFit = !hasFitOnceRef.current;
    if ((fit === "auto") || (isControlled && isFirstFit)) {
      if (markers.length === 1) {
        mapRef.current.setCenter({ lat: markers[0].lat, lng: markers[0].lng });
        mapRef.current.setZoom(zoom);
      } else {
        mapRef.current.fitBounds(bounds, 32);
      }
      hasFitOnceRef.current = true;
    }
  }, [mapReady, markers, fit, zoom]);

  // Explicit refit trigger — bumping `fitNonce` refits to current
  // markers regardless of `fit` mode. Used for "Fit Fleet" button and
  // for filter-change refits in the Fleet Map workspace.
  useEffect(() => {
    if (!mapReady || !mapRef.current || !window.google?.maps) return;
    if (fitNonce === undefined || fitNonce === null) return;
    if (markers.length === 0) return;
    const maps = window.google.maps;
    const bounds = new maps.LatLngBounds();
    markers.forEach((m) => bounds.extend({ lat: m.lat, lng: m.lng }));
    if (markers.length === 1) {
      mapRef.current.setCenter({ lat: markers[0].lat, lng: markers[0].lng });
      mapRef.current.setZoom(zoom);
    } else {
      mapRef.current.fitBounds(bounds, 32);
    }
    hasFitOnceRef.current = true;
  }, [fitNonce]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Focus a specific marker externally (asset-list → map sync).
  useEffect(() => {
    if (!mapReady || !mapRef.current || !window.google?.maps) return;
    if (!focusMarkerId) return;
    const target = markerObjs.current.find((m) => m.__aa_id === focusMarkerId);
    if (!target) return;
    const pos = target.getPosition();
    if (pos) mapRef.current.panTo(pos);
    const source = markers.find((m) => m.id === focusMarkerId);
    if (source?.info) {
      infoWindow.current.setContent(source.info);
      infoWindow.current.open({ anchor: target, map: mapRef.current });
    }
  }, [focusMarkerId, mapReady, markers]);

  // ResizeObserver → google.maps.event.trigger(map, "resize") — the
  // equivalent of Leaflet's invalidateSize() when the container box
  // changes (Edit-Mode Compact→Wide re-flow, sidebar collapse, etc.).
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    let raf = 0, followUp = 0;
    const trigger = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (mapRef.current && window.google?.maps) {
          window.google.maps.event.trigger(mapRef.current, "resize");
        }
      });
      clearTimeout(followUp);
      followUp = setTimeout(() => {
        if (mapRef.current && window.google?.maps) {
          window.google.maps.event.trigger(mapRef.current, "resize");
        }
      }, 250);
    };
    const ro = new ResizeObserver(trigger);
    ro.observe(el);
    window.addEventListener("resize", trigger);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", trigger);
      cancelAnimationFrame(raf);
      clearTimeout(followUp);
    };
  }, []);

  if (errored) return null; // Caller renders its own empty state via onError.
  return (
    <div
      ref={containerRef}
      className={containerClassName}
      style={{ width: "100%", height: "100%", ...(containerStyle || {}) }}
      data-testid="google-map-container"
    />
  );
}
